import React, { useState, useEffect } from "react"
import { Box, Accordion, AccordionSummary, AccordionDetails, Typography } from "@mui/material"

import Icon from "@mdi/react"
import { mdiInformation, mdiChevronDown } from "@mdi/js"

import ConfigurationGroupSingle from "./ConfigurationGroupSingle"
import ConfigurationGroupMulti from "./ConfigurationGroupMulti"
import ConfigurationGroupDropDown from "./ConfigurationGroupDropDown"
import Price from "components/Price"

import fetchConfigurationGroupProducts from "@secureo/common/utils/commerceTools/graphql/queries/fetchConfigurationGroupProducts"
import { netToGross } from "@secureo/common/utils/tax"

import {
	ProductConfigurationsBySku,
	ProductConfigurationsByConfigurationGroup,
} from "@secureo/common/reducers/productDetailPageReducer"
import {
	ConfigurationGroup,
	ConfigurationGroupProductsBySku,
} from "@secureo/common/typings/Product"
import { CountryCode } from "@secureo/common/typings/CountryCode"

const fetchConfigurationGroupProductsBySku = async (
	accessToken: string,
	configurationGroupProductSkus: string[],
) => {
	const configurationGroupProducts = await fetchConfigurationGroupProducts(
		accessToken,
		configurationGroupProductSkus,
		"EUR",
	)

	const configurationGroupProductsBySku = configurationGroupProducts.reduce(
		(configurationGroupProductsBySku, configurationGroupProduct) => {
			const { sku } = configurationGroupProduct

			return {
				...configurationGroupProductsBySku,
				[sku]: configurationGroupProduct,
			}
		},
		{} as ConfigurationGroupProductsBySku,
	)

	return configurationGroupProductsBySku
}

const ConfigurationGroups = ({
	isLoading = false,
	accessToken,
	configurationGroups = [],
	configurationsByConfigurationGroup,
	taxRate,
	countryCode,
	onConfigurationGroupsFetched = null,
	setSingleConfigurationGroupConfiguration,
	setMultiConfigurationGroupConfiguration,
	isLockingSystemComponent = false,
}: Props) => {
	const filteredConfigurationGroups = isLockingSystemComponent
		? configurationGroups.filter(
				({ excludeFromLockingSystemComponent }) => !excludeFromLockingSystemComponent,
		  )
		: configurationGroups
	const configurationGroupProductSkus = filteredConfigurationGroups.reduce(
		(configurationGroupProductSkus, configurationGroup) => {
			const skus = configurationGroup.items.map(({ sku }) => sku)
			return [...configurationGroupProductSkus, ...skus]
		},
		[] as string[],
	)

	const [configurationGroupProductsBySku, setConfigurationGroupProductsBySku] = useState(
		{} as ConfigurationGroupProductsBySku,
	)

	useEffect(() => {
		fetchConfigurationGroupProductsBySku(accessToken, configurationGroupProductSkus).then(
			(configurationGroupProductsBySku) => {
				setConfigurationGroupProductsBySku(configurationGroupProductsBySku)
				if (onConfigurationGroupsFetched) {
					onConfigurationGroupsFetched(configurationGroupProductsBySku)
				}
			},
		)

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [configurationGroupProductSkus.toString()])

	const numberOfConfigurationGroups = filteredConfigurationGroups.length

	return (
		<div>
			<div
				style={{
					width: "100%",
					fontSize: "12px",
					display: "flex",
					alignItems: "center",
					marginBottom: "1rem",
				}}
			>
				<Icon path={mdiInformation} size={0.8} color="#274041" />
				<Box ml={1}>
					<strong>Bitte beachten Sie:</strong> Die Anzahl des ausgewählten Zubehörs
					versteht sich pro Artikel.
				</Box>
			</div>
			{filteredConfigurationGroups.map((configurationGroup) => {
				const { name, description, type } = configurationGroup
				const selectedConfigurationsBySku: ProductConfigurationsBySku =
					configurationsByConfigurationGroup?.[name] ?? {}

				const totalConfigurationGroupGrossPriceCents = Object.values(
					selectedConfigurationsBySku,
				).reduce((totalConfigurationGroupGrossPriceCents, configuration) => {
					const { singleNetPriceCents, quantity } = configuration

					const singleGrossPriceCents = Math.round(
						netToGross(singleNetPriceCents, taxRate),
					)
					const totalGrossPriceCents = singleGrossPriceCents * quantity

					return totalConfigurationGroupGrossPriceCents + totalGrossPriceCents
				}, 0)

				return (
					<Accordion
						key={configurationGroup.name}
						defaultExpanded={numberOfConfigurationGroups === 1}
					>
						<AccordionSummary
							expandIcon={
								<Icon path={mdiChevronDown} size={1} style={{ padding: 0 }} />
							}
							aria-controls={name}
							id={`accordion-${name}`}
						>
							<h4 style={{ margin: 10 }}>
								{name}{" "}
								{totalConfigurationGroupGrossPriceCents > 0 && (
									<span style={{ color: "#505e5f" }}>
										+
										<Price
											priceInEuroCents={
												totalConfigurationGroupGrossPriceCents
											}
										/>
									</span>
								)}
							</h4>
						</AccordionSummary>

						<AccordionDetails style={{ paddingTop: 0 }}>
							<Box pb={2}>
								<p dangerouslySetInnerHTML={{ __html: description }} />
								{type === "single" && (
									<ConfigurationGroupSingle
										isLoading={isLoading}
										taxRate={taxRate}
										countryCode={countryCode}
										configurationGroup={configurationGroup}
										configurationGroupProductsBySku={
											configurationGroupProductsBySku
										}
										selectedConfigurationsBySku={selectedConfigurationsBySku}
										setSingleConfigurationGroupConfiguration={
											setSingleConfigurationGroupConfiguration
										}
									/>
								)}

								{["multi", "select-multi"].includes(type) && (
									<ConfigurationGroupMulti
										isLoading={isLoading}
										taxRate={taxRate}
										countryCode={countryCode}
										configurationGroup={configurationGroup}
										configurationGroupProductsBySku={
											configurationGroupProductsBySku
										}
										selectedConfigurationsBySku={selectedConfigurationsBySku}
										setMultiConfigurationGroupConfiguration={
											setMultiConfigurationGroupConfiguration
										}
									/>
								)}

								{type === "dropDown" && (
									<ConfigurationGroupDropDown
										isLoading={isLoading}
										taxRate={taxRate}
										countryCode={countryCode}
										configurationGroup={configurationGroup}
										configurationGroupProductsBySku={
											configurationGroupProductsBySku
										}
										selectedConfigurationsBySku={selectedConfigurationsBySku}
										setSingleConfigurationGroupConfiguration={
											setSingleConfigurationGroupConfiguration
										}
									/>
								)}
							</Box>
						</AccordionDetails>
					</Accordion>
				)
			})}
		</div>
	)
}

interface Props {
	isLoading?: boolean
	accessToken: string
	taxRate: number
	countryCode: CountryCode
	isLockingSystemComponent?: boolean
	configurationGroups: ConfigurationGroup[]
	configurationsByConfigurationGroup: ProductConfigurationsByConfigurationGroup
	onConfigurationGroupsFetched?: (
		configurationGroupProductsBySku: ConfigurationGroupProductsBySku,
	) => void
	setSingleConfigurationGroupConfiguration: (
		configurationGroupProductsBySku: ConfigurationGroupProductsBySku,
		configurationGroupName: string,
		sku: string,
	) => void
	setMultiConfigurationGroupConfiguration: (
		configurationGroupProductsBySku: ConfigurationGroupProductsBySku,
		configurationGroupName: string,
		sku: string,
		quantity: number,
	) => void
}

export default ConfigurationGroups
